import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Input, Select } from 'antd';
import {
  LinkOutlined,
  SearchOutlined,
  ExportOutlined,
  WarningOutlined,
  CreditCardOutlined,
  MobileOutlined,
  ScanOutlined,
  GlobalOutlined,
  UserOutlined,
  MailOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import shared from '../../shared.scss';
import style from './index.scss';

// StatusBadge was `$variant`-driven; the variants are modifier classes now.
const BADGE_CLASS = {
  blue: shared.badgeBlue,
  gray: shared.badgeGray,
  purple: shared.badgePurple,
  amber: shared.badgeAmber,
  red: shared.badgeRed,
  orange: shared.badgeOrange
};
const badgeClass = (variant) =>
  `${shared.statusBadge} ${BADGE_CLASS[variant] || BADGE_CLASS.gray}`;

// Related Cases (Spatial, per the Lovable design): summary pills + primary
// identifiers + filterable table of linked cases. On mock; needs BE-3 for live.

const { Option } = Select;

const IDENT_ICONS = {
  account: CreditCardOutlined,
  mobile: MobileOutlined,
  device: ScanOutlined,
  ip: GlobalOutlined,
  beneficiary: UserOutlined,
  email: MailOutlined,
  customer: IdcardOutlined
};
const identIcon = (k) => IDENT_ICONS[k] || LinkOutlined;

// Badge variants per the design: Open = bg-blue-50/text-blue-700/border-blue-200
// (read off DevTools); the rest follow the same 50/700/200 tailwind families.
const STATUS_VARIANT = {
  Open: 'blue',
  Closed: 'gray',
  'Pending Review': 'purple',
  Escalated: 'orange'
};
const DISPOSITION_VARIANT = {
  'Suspicious - Reported': 'amber',
  'Confirmed Fraud': 'red',
  'False Positive': 'gray'
};

const RelatedCasesPanel = ({ relatedCases }) => {
  const {
    note,
    summary = {},
    identifiers = [],
    cases = []
  } = relatedCases || {};
  const [search, setSearch] = useState('');
  const [identifier, setIdentifier] = useState('all');
  const [status, setStatus] = useState('all');

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return cases.filter((c) => {
      if (identifier !== 'all' && c.matched?.label !== identifier) return false;
      if (status !== 'all' && c.status !== status) return false;
      if (!q) return true;
      return [c.id, c.subject, c.matched?.value, c.assignedTo]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q));
    });
  }, [cases, search, identifier, status]);

  const columns = [
    {
      title: 'Case',
      key: 'case',
      render: (_, c) => (
        <div>
          <div className={style.caseId}>{c.id}</div>
          <div className={style.caseSubject}>{c.subject}</div>
          <div className={style.caseAlerts}>
            {c.alerts} alert{c.alerts > 1 ? 's' : ''}
          </div>
        </div>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s) => <span className={badgeClass(STATUS_VARIANT[s])}>{s}</span>
    },
    {
      title: 'FRAML Score (at creation)',
      dataIndex: 'score',
      key: 'score',
      render: (s) => (
        <span
          className={`${style.scorePill} ${
            s >= 70 ? style.scoreHigh : style.scoreMedium
          }`}
        >
          {s}
        </span>
      )
    },
    {
      title: 'Disposition',
      dataIndex: 'disposition',
      key: 'disposition',
      // Design renders a neutral badge with an em dash when there is no
      // disposition — not plain muted text.
      render: (d) => (
        <span className={badgeClass(d ? DISPOSITION_VARIANT[d] : 'gray')}>
          {d || '—'}
        </span>
      )
    },
    {
      title: 'Matched Identifier',
      dataIndex: 'matched',
      key: 'matched',
      render: (m) => {
        const Icon = identIcon(m?.icon);
        return (
          <div className={style.matchedCell}>
            <Icon />
            <div>
              <div className={style.mLabel}>{m?.label}</div>
              <div className={style.mValue}>{m?.value}</div>
            </div>
          </div>
        );
      }
    },
    // Design: both cells are td.text-foreground (NOT muted).
    { title: 'Created', dataIndex: 'created', key: 'created' },
    { title: 'Assigned To', dataIndex: 'assignedTo', key: 'assignedTo' },
    {
      title: 'Action',
      key: 'action',
      align: 'right', // design: td.text-right
      render: () => (
        <a>
          Open <ExportOutlined />
        </a>
      )
    }
  ];

  return (
    <div className={style.relatedCasesWrap}>
      <div className={style.relatedHeader}>
        <div>
          <span className={style.rcTitle}>
            <LinkOutlined />
            Related Cases
          </span>
          {note ? <p className={style.rcNote}>{note}</p> : null}
        </div>
        <div className={style.rcPills}>
          <span className={`${style.rcPill} ${style.total}`}>
            {summary.total} total
          </span>
          <span className={`${style.rcPill} ${style.active}`}>
            {summary.active} active
          </span>
          <span className={style.rcPill}>{summary.closed} closed</span>
          <span className={`${style.rcPill} ${style.fraud}`}>
            <WarningOutlined />
            {summary.confirmedFraud} confirmed fraud
          </span>
        </div>
      </div>

      <div className={style.identifiersBox}>
        <div className={style.identLabel}>
          Primary Identifiers from Current Alert
        </div>
        <div className={style.identifierCards}>
          {identifiers.map((id) => {
            const Icon = identIcon(id.icon);
            return (
              // Design renders each identifier as a <button>
              // (hover:border-primary/50)
              <button type="button" className={style.idCard} key={id.id}>
                <div className={style.idHead}>
                  <Icon className={style.idIcon} />
                  <span className={style.idCount}>{id.count}</span>
                </div>
                <div className={style.idLabel}>{id.label}</div>
                <div className={style.idValue}>{id.value}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className={style.relatedFilters}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search case ID, subject, identifier value, analyst..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 360 }}
        />
        <Select
          value={identifier}
          onChange={setIdentifier}
          style={{ width: 170 }}
          getPopupContainer={(t) => t.parentNode}
        >
          <Option value="all">All identifiers</Option>
          {identifiers.map((id) => (
            <Option key={id.id} value={id.label}>
              {id.label}
            </Option>
          ))}
        </Select>
        <Select
          value={status}
          onChange={setStatus}
          style={{ width: 150 }}
          getPopupContainer={(t) => t.parentNode}
        >
          <Option value="all">All statuses</Option>
          <Option value="Open">Open</Option>
          <Option value="Closed">Closed</Option>
          <Option value="Pending Review">Pending Review</Option>
          <Option value="Escalated">Escalated</Option>
        </Select>
        <span className={style.rcCount}>
          Showing {rows.length} of {cases.length}
        </span>
      </div>

      <div className={shared.dataTable}>
        <Table
          size="middle"
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={false}
        />
      </div>
    </div>
  );
};

RelatedCasesPanel.propTypes = {
  relatedCases: PropTypes.object
};

export default RelatedCasesPanel;
